import { LIBRARY_DOCUMENT_DETAIL } from '@/constant/QueryKeyName';
import { reverseHashTypeUpload } from '@/constant/fileType';
import { generateQuery } from '@/helpers/pathName';
import {
  getFirst150Characters,
  removeBreakLinesAndSpaces,
} from '@/helpers/stringHelpers';
import useGetLatestSubscription from '@/hooks/subscription/useGetLatestSubscription';
import { useSEO } from '@/hooks/useSEO';
import { IResponseDetailMaterial } from '@/interfaces/doccen-lib/contentManagement.interface';
import { getDoccenLibMaterial } from 'apis/tms/library/material.api';
import { AxiosError } from 'axios';
import cookieCommons from 'common/cookieCommons';
import { checkSubscriptionResource } from 'common/subscription/checkSubscriptionResource';
import { PATH_NAME } from 'configs/pathName';
import { Avatar } from 'design-system/components/Avatar';
import { Typography } from 'design-system/components/Typography';
import BaseLayout from 'layouts';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { NextPageWithLayout } from '../page';

const DynamicDocumentDetail = dynamic(
  () =>
    import('@/components/elements/doccen-lib/document-detail/DocumentDetail'),
  {
    ssr: false,
  }
);

const DocumentDetailHeader = dynamic(
  () =>
    import(
      '@/components/elements/doccen-lib/document-detail/DocumentDetailHeader'
    ),
  {
    ssr: false,
  }
);

const DocumentDetailMoreInformation = dynamic(
  () =>
    import(
      '@/components/elements/doccen-lib/document-detail/DocumentDetailMoreInformation'
    ),
  {
    ssr: false,
  }
);

const BlankRequireLogin = dynamic(
  () =>
    import(
      '@/components/elements/doccen-lib/document-detail/BlankRequiredLogin'
    )
);

const DocumentPage: NextPageWithLayout<{}> = () => {
  const router = useRouter();

  const { did: documentId } = router.query;

  const { data: material, isFetching } = useQuery<
    IResponseDetailMaterial,
    AxiosError,
    IResponseDetailMaterial
  >(
    [LIBRARY_DOCUMENT_DETAIL, documentId],
    () => getDoccenLibMaterial(documentId as string),
    {
      enabled: !!documentId,
      onError(err) {
        if (err.response?.status === 401) {
          router.push({
            pathname: PATH_NAME.LOGIN,
            query: { ...generateQuery(router) },
          });
          return;
        }

        if (err.response?.status === 403) {
          router.push({
            pathname: PATH_NAME.FORBIDDEN,
          });
          return;
        }
      },
    }
  );

  const { data: latestSubscription } = useGetLatestSubscription({});

  const isLockedPDF =
    !checkSubscriptionResource(latestSubscription).document.accessToDocument &&
    !material?.material.isOwner;

  return (
    <div className="flex w-full flex-col items-center">
      <DocumentDetailHeader material={material} />
      <div className="flex w-full max-w-container gap-4 px-0 md:px-5">
        <div className="flex h-full w-full flex-1 flex-col bg-white p-4 text-left md:p-6">
          <div className="flex flex-col gap-2">
            <Typography variant="display2">
              {material?.material.name}
            </Typography>
            <div className="my-4 flex items-center gap-2">
              <Link href={material?.material.owner.profileUrl || ''} passHref>
                <Avatar
                  type="image"
                  src={material?.material.owner.avatar}
                  size={24}
                  className="cursor-pointer"
                />
              </Link>
              <div>
                <Typography
                  variant="input-label"
                  className="text-Text/LightBackground/Secondary"
                >
                  Được tạo bởi:
                </Typography>

                <Link href={material?.material.owner.profileUrl || ''}>
                  <a className="hover:underline">
                    <Typography
                      variant="body3"
                      className="line-clamp-2 flex items-center gap-1"
                    >
                      {material?.material.owner.academicRank &&
                        `${material?.material.owner.academicRank}. `}
                      {material?.material.owner.name}
                    </Typography>
                  </a>
                </Link>
              </div>
            </div>
          </div>
          {isFetching ? ( //this is essential for unmouting PDFDisplay as it cannot render pdf properly without re-mounting
            <div>Loading</div>
          ) : material?.material.url ? (
            <DynamicDocumentDetail
              document={{
                uri: material?.material.url || '',
                fileType:
                  reverseHashTypeUpload[
                    material?.material.metadata.extension.slice(
                      1
                    ) as keyof typeof reverseHashTypeUpload
                  ],
                fileName: material?.material.metadata.fileName,
                options: {
                  lockOptions: {
                    enableLock: isLockedPDF,
                    skipPages: 2,
                  },
                },
              }}
            />
          ) : (
            <BlankRequireLogin />
          )}
        </div>
        <DocumentDetailMoreInformation
          document={material}
          latestSubscription={latestSubscription}
        />
      </div>
    </div>
  );
};

export default DocumentPage;

DocumentPage.getLayout = (page) => {
  return <BaseLayout>{page}</BaseLayout>;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  cookieCommons.setContext(context);
  const documentId = context.params?.did;

  try {
    const data = await getDoccenLibMaterial(documentId as string);

    const seo = JSON.parse(
      JSON.stringify(
        useSEO(data.material.name, {
          description: getFirst150Characters(
            removeBreakLinesAndSpaces(data.material.description)
          ),
          image:
            data.material.thumbnailUrl ||
            'https://i.pinimg.com/originals/68/3a/0a/683a0afbeedeaae759c5676861a787b9.jpg',
        })
      )
    );
    return {
      props: {
        data,
        seo,
      },
    };
  } catch (err) {
    console.log(err);

    return {
      redirect: {
        destination: PATH_NAME.NOT_FOUND,
        permanent: false,
      },
    };
  }
};
